<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.visibility.location.modal.new.buttonText')"
      @click="openVisibilityLocationModal = true"
    />
    <export
      url="/visibility_locations/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="`${$t('csv.title.visibilityLocations')}_${$t(`pages.visibility.bicycle.visibilityLocationStatus.${props.variant}`)}`"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <visibility-location-modal
    v-model:open="openVisibilityLocationModal"
    v-model:visibility-location="editedVisibilityLocation"
    @saved="refetch"
  />
  <div class="main-area h-full">
    <visibility-location-map
      v-if="records?.length"
      class="ml-12"
      :busy="loading"
      :points="pointsForMap"
    />
    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.visibility.location.tableHeader') }}
      </div>
      <div class="flex flex-col">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          v-model:checked-ids-list="checkedIdsList"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :actions="actions"
          :choosable="true"
        />
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { useTableFields } from '@hooks/table-configuration';
import { useQueryRef } from '@hooks/url';
import { format } from 'date-fns';
import VisibilityLocationModal from './VisibilityLocationModal.vue';
import {
  type HydraCollection,
  type InterestZone,
  type Region,
  VisibilityLocationStatus,
  VisibilityLocationViewMode,
} from '@/entities/Entities';
import VisibilityLocationMap from './VisibilityLocationMap.vue';

interface Props {
  variant: VisibilityLocationViewMode;
}
const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);

const filter = useQueryRef('filter', {
  ...(props.variant === VisibilityLocationViewMode.ACTIVE
    ? { status: VisibilityLocationStatus.ACTIVE } : {}),
  ...(props.variant === VisibilityLocationViewMode.INACTIVE
    ? { status: VisibilityLocationStatus.INACTIVE } : {}),
  ...(props.variant === VisibilityLocationViewMode.ARCHIVED
    ? { status: VisibilityLocationStatus.ARCHIVED } : {}),
});

const openVisibilityLocationModal = ref(false);
const editedVisibilityLocation = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [, { loading: deleting }] = useDelete(t('pages.visibility.location.actions.deleteWarning'));

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.visibility.location.actions.edit'),
    action: (item: InterestZone) => {
      openVisibilityLocationModal.value = true;
      editedVisibilityLocation.value = { ...item };
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.visibility.location.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.visibility.location.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'name',
    title: t('visibility.location.name'),
    group: t('pages.visibility.location.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.visibility.location.filterGroups.general'),
      options: {
        label: t('visibility.location.name'),
      },
    },
  },
  {
    key: 'region.name',
    title: t('visibility.location.region'),
    group: t('pages.visibility.location.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'region',
      type: 'dropdown',
      group: t('pages.visibility.location.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('visibility.location.region'),
      },
    },
  },
  {
    key: 'comment',
    title: t('visibility.location.comment'),
    group: t('pages.visibility.location.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.visibility.location.filterGroups.general'),
      options: {
        label: t('visibility.location.comment'),
      },
    },
  },
  {
    key: 'radius',
    title: t('visibility.location.radius'),
    group: t('pages.visibility.location.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.visibility.location.filterGroups.general'),
      options: {
        label: t('visibility.location.radius'),
      },
    },
  },
  {
    key: 'quota',
    title: t('visibility.location.quota'),
    group: t('pages.visibility.location.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.visibility.location.filterGroups.general'),
      options: {
        label: t('visibility.location.quota'),
      },
    },
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    group: t('pages.visibility.location.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.location.filterGroups.general'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    group: t('pages.visibility.location.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.location.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<InterestZone>>('/visibility_locations', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);

const checkedIdsList = ref<string[]>([]);
const pointsForMap = computed(() => checkedIdsList.value.map((id) => records.value?.find((record) => record.id === id)));
</script>

